import React, { FC } from 'react'

import styled from 'styled-components'
import { Skeleton } from 'antd'
import { SkeletonNodeProps } from 'antd/es/skeleton/Node'
import { useMediaQuery } from 'react-responsive'

import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import { CampaignStatusEnum } from 'src/Types/campaigns'
import { CampaignPayment } from 'src/Redux/artistOrderApi/campaignPayment'
import { ArtistReviewFeedbackBody } from 'src/Redux/artistSpotifyCampaignReviewApi'
import { CampaignReview } from 'src/Hooks/useCampaignDetailsReviews'

import { InfoBlock } from '../InfoBlock'
import { ReviewsList } from '../ReviewsList'
import { StatsBlockContainer } from '../StatsBlockContainer'
import { CampaignUpsell } from '../CampaignUpsell'
import { OnFavoriteCuratorParams } from '../..'
import { IntercomMessageEnum } from '../CampaignUpsell/CampaignUpsellCard/helpers'

const ResultWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex: 1 0 0;
  align-self: stretch;
`

const StyledSkeleton = styled(({ className, ...rest }: SkeletonNodeProps) => (
  <Skeleton.Input {...rest} rootClassName={className} active />
))`
  &.ant-skeleton-block {
    border-radius: 16px;
  }
  &.ant-skeleton {
    flex-grow: 1 !important;
    @media (max-width: ${MAX_MOBILE_WIDTH}) {
      width: 100% !important;
    }
  }
  & > .ant-skeleton-input {
    width: 100% !important;

    height: 240px !important;
    border-radius: 16px !important;
    background: ${({ theme }) => theme.color.lightGrey} !important;

    @media (max-width: ${MAX_MOBILE_WIDTH}) {
      width: 100% !important;
    }
  }
`

interface ResultSectionProps {
  loading: boolean
  reviews?: CampaignReview[]
  reviewsCount?: number
  placementsCount?: number
  campaignStatus?: CampaignStatusEnum
  payment?: CampaignPayment | null
  userId?: number
  campaignId?: number
  trackName?: string
  trackAuthorName?: string
  trackCoverUrl?: string
  startDate?: string | null
  onStartCampaignClick?: () => void
  onOpenIntercom: (message: IntercomMessageEnum) => void
  onRelaunch: (isCTA?: boolean) => void
  onUpsellMetaAds: () => void
  isRelaunchAvailable?: boolean
  onSubmitLikeDislike: (feedbackBody: ArtistReviewFeedbackBody) => void
  onFavoriteCurator: (params: OnFavoriteCuratorParams) => void
  onAddFavoriteCuratorLoading: boolean
  isManualMatching: boolean
}

export const ResultSection: FC<ResultSectionProps> = ({
  reviews,
  loading,
  reviewsCount,
  placementsCount,
  campaignStatus,
  payment,
  userId,
  campaignId,
  trackAuthorName,
  trackCoverUrl,
  trackName,
  startDate,
  onOpenIntercom,
  onRelaunch,
  onUpsellMetaAds,
  onStartCampaignClick,
  isRelaunchAvailable,
  onSubmitLikeDislike,
  onFavoriteCurator,
  onAddFavoriteCuratorLoading,
  isManualMatching,
}) => {
  const isMobile = useMediaQuery({ query: `(max-width: ${MAX_MOBILE_WIDTH})` })
  if (loading) {
    return <StyledSkeleton />
  }

  if (!campaignStatus) {
    return null
  }

  const showCampaignUpsell =
    campaignStatus === CampaignStatusEnum.ENDED &&
    typeof placementsCount === 'number' &&
    !isManualMatching

  return (
    <ResultWrapper>
      {showCampaignUpsell && !isMobile && (
        <CampaignUpsell
          onOpenIntercom={onOpenIntercom}
          onRelaunch={onRelaunch}
          onUpsellMetaAds={onUpsellMetaAds}
          placementsCount={placementsCount}
          isRelaunchAvailable={isRelaunchAvailable}
          campaignId={campaignId}
        />
      )}
      <StatsBlockContainer
        reviewsCount={reviewsCount}
        placementsCount={placementsCount}
        campaignStatus={campaignStatus}
      />
      {showCampaignUpsell && isMobile && (
        <CampaignUpsell
          onOpenIntercom={onOpenIntercom}
          onRelaunch={onRelaunch}
          onUpsellMetaAds={onUpsellMetaAds}
          placementsCount={placementsCount}
          isRelaunchAvailable={isRelaunchAvailable}
          campaignId={campaignId}
        />
      )}

      <InfoBlock
        startDate={startDate}
        campaignStatus={campaignStatus}
        campaignId={campaignId}
        payment={payment}
        reviewsCount={reviews?.length}
        onStartCampaignClick={onStartCampaignClick}
      />
      <ReviewsList
        reviews={reviews}
        userId={userId}
        campaignId={campaignId}
        trackName={trackName}
        trackAuthorName={trackAuthorName}
        trackCoverUrl={trackCoverUrl}
        onSubmitLikeDislike={onSubmitLikeDislike}
        onFavoriteCurator={onFavoriteCurator}
        onAddFavoriteCuratorLoading={onAddFavoriteCuratorLoading}
        isManualMatching={isManualMatching}
      />
    </ResultWrapper>
  )
}
